<script setup lang="ts">
/**
 * Sidebar — primary navigation panel.
 *
 * Architecture: derives entirely from the route table. Every entry in
 * SCREENS that has a `meta.section` AND isn't dynamic AND passes the
 * privilege gate appears in the nav. No second nav-config to drift
 * out of sync.
 *
 * Per Sprint 2 Batch B refactor:
 *   - Adds `meta.navOrder?: number` so a section's items can have
 *     deliberate ordering (e.g. "Categories" before "Services" before
 *     "VOD library" in the content section). Items without navOrder
 *     fall through to alphabetical-by-route-name.
 *
 *   - Adds `meta.hideFromNav?: boolean` for routes that exist but
 *     shouldn't be in the sidebar (typically detail routes whose
 *     parent already is, e.g. `/commerce/bundles/{id}`).
 *
 *   - Hardens i18n fallback: when `$t(meta.title)` returns the key
 *     unchanged (a missed translation), we render a humanised version
 *     of the route name instead of the raw key. Guards against the
 *     class of bug Manus saw in Batch A where new routes' titles
 *     leaked through as `nav.cmsUsers`.
 *
 *   - Uses `meta.icon` directly as the lucide-vue-next component name.
 *
 * Privilege gate: routes whose `meta.privilege` the current admin
 * doesn't hold are filtered out (hidden, not greyed). The route guard
 * is the actual security; this is just nav cleanup.
 */
import { computed, h } from 'vue'
import { useRoute, RouterLink, type RouteRecordRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import * as Lucide from 'lucide-vue-next'
import { SECTIONS, SCREENS, type SectionId } from '@/router'
import { useLensStore } from '@/stores/lens'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const lens = useLensStore()
const auth = useAuthStore()
const { t, te } = useI18n()

interface NavItem {
  routeName: string
  path: string
  label: string  // already-resolved display string
  icon: string
  badge?: string
  navOrder?: number
}

interface NavSection {
  id: SectionId
  label: string  // already-resolved
  items: NavItem[]
}

/**
 * Resolve a route's display label. If the i18n key resolves, use it;
 * otherwise fall back to a humanised route name so the nav doesn't
 * show literal `nav.fooBar` strings to users.
 */
function resolveLabel(meta: RouteRecordRaw['meta'], fallbackName: string): string {
  const key = meta?.title
  if (typeof key === 'string' && te(key)) {
    return t(key)
  }
  // Humanise the route name as a last resort: 'cms-users' → 'Cms users'
  const human = fallbackName
    .replace(/[-_]/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
  return human
}

const sections = computed<NavSection[]>(() => {
  const bySection = new Map<SectionId, NavItem[]>()

  for (const screen of SCREENS) {
    const meta = screen.meta
    if (!meta) continue
    if (meta.hideFromNav) continue
    if (screen.path.includes(':')) continue

    const sectionId = meta.section as SectionId
    if (!sectionId) continue

    if (meta.privilege && !auth.hasPrivilege(meta.privilege as string)) {
      continue
    }

    let bucket = bySection.get(sectionId)
    if (!bucket) {
      bucket = []
      bySection.set(sectionId, bucket)
    }

    bucket.push({
      routeName: screen.name as string,
      path: screen.path,
      label: resolveLabel(meta, screen.name as string),
      icon: meta.icon as string,
      badge: meta.badge as string | undefined,
      navOrder: meta.navOrder as number | undefined,
    })
  }

  // Sort items within each bucket: navOrder asc, then route name asc.
  for (const items of bySection.values()) {
    items.sort((a, b) => {
      const ao = a.navOrder ?? Number.MAX_SAFE_INTEGER
      const bo = b.navOrder ?? Number.MAX_SAFE_INTEGER
      if (ao !== bo) return ao - bo
      return a.routeName.localeCompare(b.routeName)
    })
  }

  // Order sections by current lens; sections not in the lens order
  // fall to the end in their declared SECTIONS order.
  const lensOrder = lens.current.order as SectionId[]
  const inLens = new Set(lensOrder)
  const tail = SECTIONS.map((s) => s.id).filter((id) => !inLens.has(id))
  const allOrder = [...lensOrder, ...tail]

  return allOrder
    .map((id) => {
      const items = bySection.get(id) ?? []
      if (items.length === 0) return null
      const def = SECTIONS.find((s) => s.id === id)
      if (!def) return null
      const label = te(def.labelKey) ? t(def.labelKey) : id
      return { id, label, items }
    })
    .filter((s): s is NavSection => s !== null)
})

function iconComponent(name: string) {
  // Convert kebab-case to PascalCase for lucide lookup.
  const pascal = name
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const found = (Lucide as Record<string, any>)[pascal]
  return found ?? Lucide.Circle
}

function isActive(routeName: string): boolean {
  return route.name === routeName
}

const userInitials = computed(() => {
  const name = auth.user?.userName ?? ''
  return name.slice(0, 2).toUpperCase() || '?'
})

function navIcon(name: string) {
  return h(iconComponent(name), { size: 14, 'aria-hidden': true })
}
</script>

<template>
  <aside
    class="w-60 h-screen bg-bg border-r border-border flex flex-col shrink-0
           overflow-hidden"
  >
    <!-- Brand mark -->
    <div
      class="h-14 px-4 flex items-center gap-2 border-b border-border shrink-0"
    >
      <div
        class="w-7 h-7 rounded bg-brand flex items-center justify-center
               text-text-1 font-bold text-sm shadow-glow"
      >S</div>
      <span class="font-semibold text-text-1 tracking-tight">Streama</span>
      <span class="text-text-4 text-xs font-mono ml-auto">CMS</span>
    </div>

    <!-- Nav body -->
    <nav class="flex-1 overflow-y-auto py-3 px-2">
      <div
        v-for="section in sections"
        :key="section.id"
        class="mb-4"
      >
        <p
          class="px-2 mb-1 text-[10px] font-bold uppercase tracking-wider text-text-5"
        >{{ section.label }}</p>
        <RouterLink
          v-for="item in section.items"
          :key="item.routeName"
          :to="{ name: item.routeName }"
          custom
          v-slot="{ navigate }"
        >
          <button
            type="button"
            :class="[
              'w-full flex items-center gap-2 px-2 py-1.5 text-xs font-medium rounded',
              'transition-colors text-left',
              isActive(item.routeName)
                ? 'bg-brand-soft text-text-1 border border-brand-line'
                : 'text-text-3 hover:bg-surface-2 hover:text-text-1 border border-transparent',
            ]"
            @click="navigate"
          >
            <component :is="navIcon(item.icon)" />
            <span class="flex-1 truncate">{{ item.label }}</span>
            <span
              v-if="item.badge"
              :class="[
                'text-[9px] font-mono px-1 py-0.5 rounded-sm',
                item.badge === 'LIVE'
                  ? 'bg-green-soft text-green'
                  : 'text-text-4',
              ]"
            >{{ item.badge }}</span>
          </button>
        </RouterLink>
      </div>
    </nav>

    <!-- Account footer -->
    <div
      class="px-3 py-2.5 border-t border-border flex items-center gap-2 shrink-0"
    >
      <div
        class="w-7 h-7 rounded-full bg-surface-3 text-text-2 flex items-center justify-center
               text-[10px] font-semibold uppercase"
      >{{ userInitials }}</div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium text-text-1 m-0 truncate">
          {{ auth.user?.userName ?? '—' }}
        </p>
        <p class="text-[10px] text-text-4 m-0 truncate">
          {{ auth.user?.external ? $t('iam.type.external') : $t('iam.type.internal') }}
        </p>
      </div>
    </div>
  </aside>
</template>
